// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The typed board model (spec §6.2).
 *
 * This is a *view* over the parse tree: every object holds a reference to its
 * original node, never a copy. The merge engine depends on that so it can
 * splice original nodes and carry unknown children through verbatim.
 */

import {
  atoms,
  get,
  getAll,
  getNums,
  getStr,
  getUuid,
  isNode,
  parse,
  type Node,
} from '../sexp/index.ts';
import { formatBBox, unionAll, type MaybeBBox } from './bbox.ts';
import { LayerTable, resolveLayers } from './layers.ts';
import {
  BOARD_PROPERTY_TOKEN,
  GLOBAL_BLOCKS,
  HEADER_FIELDS,
  KNOWN_OBJECT_TYPES,
  MEMBER_CONTAINER_TYPES,
  footprintLibId,
  footprintPath,
  footprintReference,
  footprintValue,
  groupMembers,
  isLocked,
  netNodes,
  netReference,
  objectExtent,
} from './objects.ts';
import { SpatialGrid } from './spatial.ts';

export interface BoardObject {
  /** Stable identity: `path` for footprints that have one, else the UUID. */
  id: string;
  /** How `id` was derived — surfaced in the UI because it changes match quality. */
  idSource: 'path' | 'uuid' | 'synthetic';
  uuid: string | undefined;
  /** Token name, e.g. `segment`. */
  type: string;
  /** False for tokens outside the recognised set (spec §6.2). */
  known: boolean;
  layers: string[];
  bbox: MaybeBBox;
  bboxApproximate: boolean;
  /** Resolved through the file's own net table — never a raw index (spec §3.1). */
  netName: string | undefined;
  /** Reference into the parse tree. Not a copy. */
  node: Node;
  /** Position among the root's children, for stable ordering. */
  index: number;

  // Footprint-only
  reference?: string;
  value?: string;
  libId?: string;
  path?: string;
  locked?: boolean;
  // Group-only
  members?: string[];
}

export interface BoardWarning {
  kind: 'duplicate-uuid' | 'missing-uuid' | 'net-name-mismatch' | 'unresolved-group-member' | 'unknown-net-index';
  message: string;
  objectId?: string;
}

export interface BoardStats {
  objects: number;
  byType: Record<string, number>;
  unknownObjects: number;
  approximateBoxes: number;
}

export class Board {
  readonly fileName: string;
  readonly root: Node;
  readonly version: string | undefined;
  readonly generator: string | undefined;
  readonly generatorVersion: string | undefined;
  readonly layerTable: LayerTable;

  /**
   * Net index → name, as declared by *this* file. Never shared across files.
   * Empty for KiCad 10, which dropped the table and names nets inline.
   */
  readonly netNameByIndex: Map<number, string>;
  readonly netIndexByName: Map<string, number>;
  /**
   * Every net name actually referenced by an object.
   *
   * For KiCad ≤9 this is a subset of the table. For KiCad 10 there is no table,
   * so this is the only record of which nets exist — and without it every
   * net-name comparison on a KiCad 10 board would compare two empty sets and
   * pass vacuously.
   */
  readonly netNamesInUse: Set<string>;

  readonly objects: BoardObject[];
  readonly byId: Map<string, BoardObject>;
  readonly byUuid: Map<string, BoardObject>;
  readonly byType: Map<string, BoardObject[]>;
  readonly byLayer: Map<string, BoardObject[]>;
  readonly grid: SpatialGrid;

  /** Global board state that belongs to no region (spec §3.7). */
  readonly globalBlocks: Map<string, Node>;
  /** Board text variables, whichever spelling the file uses. */
  readonly boardProperties: Map<string, string>;
  readonly warnings: BoardWarning[];

  private constructor(init: {
    fileName: string;
    root: Node;
    layerTable: LayerTable;
    netNameByIndex: Map<number, string>;
    netIndexByName: Map<string, number>;
    objects: BoardObject[];
    globalBlocks: Map<string, Node>;
    boardProperties: Map<string, string>;
    warnings: BoardWarning[];
    netNamesInUse: Set<string>;
  }) {
    this.fileName = init.fileName;
    this.root = init.root;
    this.layerTable = init.layerTable;
    this.netNameByIndex = init.netNameByIndex;
    this.netIndexByName = init.netIndexByName;
    this.objects = init.objects;
    this.globalBlocks = init.globalBlocks;
    this.boardProperties = init.boardProperties;
    this.warnings = init.warnings;
    this.netNamesInUse = init.netNamesInUse;

    this.version = getStr(init.root, 'version');
    this.generator = getStr(init.root, 'generator');
    this.generatorVersion = getStr(init.root, 'generator_version');

    this.byId = new Map();
    this.byUuid = new Map();
    this.byType = new Map();
    this.byLayer = new Map();
    for (const o of init.objects) {
      if (!this.byId.has(o.id)) this.byId.set(o.id, o);
      if (o.uuid !== undefined && !this.byUuid.has(o.uuid)) this.byUuid.set(o.uuid, o);
      let t = this.byType.get(o.type);
      if (!t) this.byType.set(o.type, (t = []));
      t.push(o);
      for (const layer of o.layers) {
        let l = this.byLayer.get(layer);
        if (!l) this.byLayer.set(layer, (l = []));
        l.push(o);
      }
    }
    this.grid = SpatialGrid.build(init.objects);
  }

  static parse(text: string, fileName = 'board.kicad_pcb'): Board {
    return Board.fromTree(parse(text, fileName), fileName);
  }

  static fromTree(root: Node, fileName = 'board.kicad_pcb'): Board {
    const warnings: BoardWarning[] = [];
    const layerTable = LayerTable.fromBoard(root);

    // --- net table (spec §3.1: names are truth, indices are file-local) ------
    const netNameByIndex = new Map<number, string>();
    const netIndexByName = new Map<string, number>();
    for (const n of getAll(root, 'net')) {
      const vals = atoms(n).map((a) => a.value);
      const idx = Number(vals[0]);
      const name = vals[1] ?? '';
      if (!Number.isFinite(idx)) continue;
      netNameByIndex.set(idx, name);
      if (!netIndexByName.has(name)) netIndexByName.set(name, idx);
    }

    // --- global blocks and text variables ------------------------------------
    const globalBlocks = new Map<string, Node>();
    for (const name of [...GLOBAL_BLOCKS, ...HEADER_FIELDS]) {
      const block = get(root, name);
      if (block) globalBlocks.set(name, block);
    }
    const boardProperties = new Map<string, string>();
    for (const p of getAll(root, BOARD_PROPERTY_TOKEN)) {
      const vals = atoms(p).map((a) => a.value);
      if (vals[0] !== undefined) boardProperties.set(vals[0], vals[1] ?? '');
    }
    const propsBlock = get(root, 'properties');
    if (propsBlock) {
      for (const p of getAll(propsBlock, BOARD_PROPERTY_TOKEN)) {
        const vals = atoms(p).map((a) => a.value);
        if (vals[0] !== undefined) boardProperties.set(vals[0], vals[1] ?? '');
      }
    }

    // --- objects --------------------------------------------------------------
    const skip = new Set<string>([...GLOBAL_BLOCKS, ...HEADER_FIELDS, 'net', BOARD_PROPERTY_TOKEN]);
    const objects: BoardObject[] = [];
    const seenUuids = new Set<string>();
    let syntheticCounter = 0;

    root.children.forEach((child, index) => {
      if (!isNode(child)) return;
      if (skip.has(child.name)) return;

      const uuid = getUuid(child);
      const type = child.name;
      const known = KNOWN_OBJECT_TYPES.has(type);
      const { box, approximate } = objectExtent(child);

      let id: string;
      let idSource: BoardObject['idSource'];
      const path = type === 'footprint' ? footprintPath(child) : undefined;
      if (path !== undefined && path !== '') {
        // Spec §3.3: the schematic link is the correct primary identity.
        id = `path:${path}`;
        idSource = 'path';
      } else if (uuid !== undefined) {
        id = `uuid:${uuid}`;
        idSource = 'uuid';
      } else {
        id = `synthetic:${type}#${syntheticCounter++}`;
        idSource = 'synthetic';
        warnings.push({
          kind: 'missing-uuid',
          message: `${type} at board index ${index} has no uuid; matching will fall back to geometry`,
          objectId: id,
        });
      }

      if (uuid !== undefined) {
        if (seenUuids.has(uuid)) {
          warnings.push({
            kind: 'duplicate-uuid',
            message: `duplicate uuid ${uuid} on a ${type}`,
            objectId: id,
          });
        }
        seenUuids.add(uuid);
      }

      const obj: BoardObject = {
        id,
        idSource,
        uuid,
        type,
        known,
        layers: resolveLayers(child, layerTable),
        bbox: box,
        bboxApproximate: approximate,
        netName: resolveNetName(child, netNameByIndex, warnings, id),
        node: child,
        index,
      };

      if (type === 'footprint') {
        obj.reference = footprintReference(child);
        obj.value = footprintValue(child);
        obj.libId = footprintLibId(child);
        if (path !== undefined) obj.path = path;
        obj.locked = isLocked(child);
      }
      if (MEMBER_CONTAINER_TYPES.has(type)) {
        obj.members = groupMembers(child);
      }

      objects.push(obj);
    });

    // --- group boxes, once every member box is known -------------------------
    const byUuid = new Map<string, BoardObject>();
    for (const o of objects) if (o.uuid !== undefined) byUuid.set(o.uuid, o);
    for (const o of objects) {
      if (!MEMBER_CONTAINER_TYPES.has(o.type) || o.members === undefined) continue;
      // Start from whatever geometry the container carries itself. For a
      // `group` that is nothing; for a `generated` tuning pattern it is the base
      // line, which is *narrower* than the meander it owns — so the member union
      // must be added to it, never substituted for it.
      const boxes: MaybeBBox[] = [o.bbox];
      let missing = 0;
      for (const m of o.members) {
        const member = byUuid.get(m);
        if (member === undefined) {
          missing++;
          continue;
        }
        boxes.push(member.bbox);
      }
      if (missing > 0) {
        warnings.push({
          kind: 'unresolved-group-member',
          message: `${o.type} ${o.uuid ?? o.id} references ${missing} member(s) that are not board-level objects`,
          objectId: o.id,
        });
      }
      o.bbox = unionAll(boxes);
      // Unresolvable members mean the true extent may exceed what we measured.
      o.bboxApproximate = missing > 0;
    }

    // Every net an object references, wherever the name comes from.
    const netNamesInUse = new Set<string>();
    for (const o of objects) {
      for (const netNode of netNodes(o.node)) {
        const ref = netReference(netNode);
        const name = ref.name ?? (ref.index === undefined ? undefined : netNameByIndex.get(ref.index));
        if (name !== undefined) netNamesInUse.add(name);
      }
    }

    return new Board({
      fileName,
      root,
      layerTable,
      netNameByIndex,
      netIndexByName,
      objects,
      globalBlocks,
      boardProperties,
      warnings,
      netNamesInUse,
    });
  }

  /**
   * Every net name this file knows about, sorted.
   *
   * The declared table where there is one, plus every name an object actually
   * references — which for KiCad 10 is the whole story.
   */
  netNames(): string[] {
    return [...this.allNetNames()].sort();
  }

  allNetNames(): Set<string> {
    const out = new Set<string>(this.netNameByIndex.values());
    for (const name of this.netNamesInUse) out.add(name);
    return out;
  }

  /** True when this file names nets inline rather than through a table (KiCad 10). */
  get usesNamedNets(): boolean {
    return this.netNameByIndex.size === 0 && this.netNamesInUse.size > 0;
  }

  /** Sorted list of every footprint `path` (spec §6.5 V2). */
  footprintPaths(): string[] {
    return this.objectsOfType('footprint')
      .map((o) => o.path)
      .filter((p): p is string => p !== undefined && p !== '')
      .sort();
  }

  /** Sorted list of every reference designator (spec §6.5 V3). */
  references(): string[] {
    return this.objectsOfType('footprint')
      .map((o) => o.reference)
      .filter((r): r is string => r !== undefined && r !== '')
      .sort();
  }

  objectsOfType(type: string): BoardObject[] {
    return this.byType.get(type) ?? [];
  }

  stats(): BoardStats {
    const byType: Record<string, number> = {};
    let unknownObjects = 0;
    let approximateBoxes = 0;
    for (const o of this.objects) {
      byType[o.type] = (byType[o.type] ?? 0) + 1;
      if (!o.known) unknownObjects++;
      if (o.bboxApproximate) approximateBoxes++;
    }
    return { objects: this.objects.length, byType, unknownObjects, approximateBoxes };
  }

  /** Overall board extent, for zoom-to-fit and region auto-detection. */
  extent(): MaybeBBox {
    return unionAll(this.objects.map((o) => o.bbox));
  }

  /** One-line human description of an object, used throughout the UI. */
  static describe(o: BoardObject): string {
    switch (o.type) {
      case 'footprint':
        return `${o.reference ?? '(no ref)'} — ${o.libId ?? 'unknown footprint'}${o.locked ? ' [locked]' : ''}`;
      case 'segment':
      case 'arc':
        return `${o.type} ${o.netName ?? '(no net)'} on ${o.layers.join(', ') || '(no layer)'}`;
      case 'via':
        return `via ${o.netName ?? '(no net)'}`;
      case 'zone':
        return `zone ${o.netName ?? '(no net)'} on ${o.layers.join(', ') || '(no layer)'}`;
      case 'group':
        return `group "${getStr(o.node, [] as string[]) ?? ''}" (${o.members?.length ?? 0} members)`;
      case 'gr_text':
        return `text "${atoms(o.node)[0]?.value ?? ''}" on ${o.layers.join(', ')}`;
      default:
        return `${o.type} on ${o.layers.join(', ') || '(no layer)'} at ${formatBBox(o.bbox)}`;
    }
  }
}

/**
 * Resolve an object's net through this file's own table.
 *
 * Zones carry both `(net N)` and `(net_name "X")`; if those disagree the file is
 * internally inconsistent, which is worth a warning but the *index* is what
 * KiCad honours, so the table lookup wins.
 */
function resolveNetName(
  n: Node,
  netNameByIndex: Map<number, string>,
  warnings: BoardWarning[],
  objectId: string,
): string | undefined {
  const netNode = get(n, 'net');
  if (netNode === undefined) return undefined;

  const ref = netReference(netNode);
  // KiCad 10 names the net inline; there is no table and nothing to resolve.
  if (ref.index === undefined) return ref.name;

  const idx = ref.index;
  const resolved = netNameByIndex.get(idx);
  if (resolved === undefined) {
    warnings.push({
      kind: 'unknown-net-index',
      message: `${n.name} references net index ${idx}, which is not in the net table`,
      objectId,
    });
    return undefined;
  }
  const declared = getStr(n, 'net_name');
  if (declared !== undefined && declared !== resolved) {
    warnings.push({
      kind: 'net-name-mismatch',
      message: `${n.name} has (net ${idx}) = "${resolved}" but (net_name "${declared}"); using the net table`,
      objectId,
    });
  }
  return resolved;
}
